/* ------------------------------------------------------------------
   CareOptions — single source of truth for the "home experience" rollout
   plus the canonical care-option visibility resolver.

   FORCE_UNIVERSAL_HOME is the one switch that flips CareHome from opt-in
   (partner.enable_home_screen) to the default for ALL partners. It is
   intentionally false during the validation checkpoint so partners without
   the toggle see ZERO change. Flipping it to true — and later deleting the
   partner flag reads — makes the home experience universal.

   resolve_care_options mirrors CareSelection.render_selection_buttons +
   the prop expressions in HomeScreen.render_care_selection, so the CareHome
   landing surfaces exactly the options the CareSelection button list would
   have (same visibility rules, same live-vs-async chat resolution). Keep the
   two in sync — or, post-flip, retire the CareSelection button list, which
   is unreachable once Phase 1 is always CareHome.
   ------------------------------------------------------------------ */

const FORCE_UNIVERSAL_HOME = false;

// Whether the CareHome experience should be shown for this partner.
const should_show_home = (partner) => {
  if (FORCE_UNIVERSAL_HOME) { return true; }
  return partner && partner.enable_home_screen === true ? true : false;
};

// Compute which care options the landing should surface + the resolved chat
// type. Inputs are the already-composited flags from HomeScreen state.
const resolve_care_options = (opts) => {
  opts = opts || {};

  let online_providers_available = opts.online_providers_available === true;
  let live_video_available       = opts.live_video_available === true;
  let hide_chat                  = opts.hide_chat_option === true;
  let hide_video                 = opts.hide_video_option === true;
  let hide_training              = opts.hide_training_option === true;
  let training_enabled           = opts.training_enabled === true;

  let show_live_chat  = online_providers_available && !hide_chat;
  let show_async_chat = !online_providers_available && !hide_chat;

  return {
    show_live_chat:  show_live_chat,
    show_async_chat: show_async_chat,
    show_chat:       show_live_chat || show_async_chat,
    show_video:      !hide_video,                          // scheduled video
    show_live_video: live_video_available && !hide_video,  // dormant until live_video_enabled
    show_training:   training_enabled && !hide_training,
    chat_type:       online_providers_available ? 'LIVE_CHAT' : 'ASYNC_CHAT',
    training_credits:        opts.training_credits || 0,
    airtable_access_enabled: opts.airtable_access_enabled === true,
    training_access_url:     opts.training_access_url || ''
  };
};

const has_any_option = (care_options) => {
  if (!care_options) { return false; }
  return !!(care_options.show_video || care_options.show_live_video ||
            care_options.show_chat || care_options.show_training);
};

const CareOptions = {
  FORCE_UNIVERSAL_HOME,
  should_show_home,
  resolve_care_options,
  has_any_option
};

export default CareOptions;
